import { toast } from "react-toastify";
import api_public from "../../services/api_public";
import api from "../../services/api";
import new_api_public from "../../services/newApi/api_public";
import new_api from "../../services/newApi/api";
import type {
  ICompletePatientRegistrationParams,
  ICompletePatientRegistrationResponse,
  IConvenio,
  ICreateDoctorParams,
  ICreateDoctorResponse,
  ICreatePatientParams,
  ICreatePatientResponse,
  ICreateUserConvenioParams,
  ICreateUserConvenioResponse,
  ICreateUserHospitalParams,
  ICreateUserHospitalResponse,
  IRegisterPatientAddressParams,
  IPatientRegistrationLookupParams,
  IPatientRegistrationLookupResponse,
  ISendPatientValidationCodeParams,
  ISendPatientValidationCodeResponse,
  ISpeciality,
  IValidatePatientValidationCodeParams,
  IValidatePatientValidationCodeResponse,
} from "./types";
import type { AxiosResponse } from "axios";
import type { IHospital } from "../Hospitals/types";

export function createRegisterUtils() {
  return {
    async createPatient(data: ICreatePatientParams) {
      try {
        const response = await api_public.post<ICreatePatientResponse>(
          `patient/register`,
          data,
        );
        if (response?.data?.message) {
          toast.success(response?.data?.message);
        }
        return response;
      } catch (error: any) {
        console.error(error);
        if (error?.response?.data?.message) {
          toast.error(error?.response?.data?.message);
        }
      }
    },

    async createDoctor(data: ICreateDoctorParams) {
      try {
        const response = await api_public.post<ICreateDoctorResponse>(
          `doctor/register`,
          data,
        );
        if (response?.data?.message) {
          toast.success(response?.data?.message);
        }
        return response;
      } catch (error: any) {
        console.error(error.response?.data?.message);
        if (error?.response?.data?.message) {
          toast.error(error?.response?.data?.message);
        }
      }
    },

    async createUserHospital(
      data: ICreateUserHospitalParams,
    ): Promise<AxiosResponse<ICreateUserHospitalResponse, any>> {
      try {
        const response = await api_public.post<ICreateUserHospitalResponse>(
          `user/hospital/register`,
          data,
        );
        return response;
      } catch (error: any) {
        if (error?.response?.data?.message) {
          toast.error(error?.response?.data?.message);
        }
        return error;
      }
    },

    async createPartner(data: any) {
      try {
        const response = await api_public.post(`partner/register`, data);
        if (response?.data?.message) {
          toast.success(response?.data?.message);
        }
        return response;
      } catch (error: any) {
        if (error?.response?.data?.message) {
          toast.error(error?.response?.data?.message);
        }
      }
    },

    async speciality() {
      try {
        const response = await api_public.get<ISpeciality[]>(
          `doctor/speciality`,
        );
        return response;
      } catch (error: any) {

      }
    },

    async hospitalsList() {
      try {
        const response = await api_public.get<IHospital[]>(`hospitals/list`);
        return response;
      } catch (error: any) {

      }
    },

    async allHospitalsButAmbList() {
      try {
        const response = await api_public.get<IHospital[]>(
          `/register/hospitals/list`,
        );
        return response;
      } catch (error: any) {

      }
    },

    async convenioList() {
      // Uses the authenticated newApi client: it attaches Authorization only when
      // a valid access token exists in the session, and lets the request through
      // unauthenticated otherwise. This is shared by the public onboarding flow
      // (no token) and the "Novo Usuário Convênio" screen (logged-in convenio user),
      // so the backend can apply its user-scoped filtering when a token is present.
      return await new_api.get<IConvenio[]>(`/health-insurance`, { params: { type: 6 } });
    },

    async createUserConvenio(
      data: ICreateUserConvenioParams,
    ): Promise<AxiosResponse<ICreateUserConvenioResponse, any> | undefined> {
      try {
        const response = await api.post<ICreateUserConvenioResponse>(
          `user/convenio/register`,
          data,
        );
        return response;
      } catch (error: any) {
        if (error?.response?.data?.message) {
          toast.error(error?.response?.data?.message);
        }
        return undefined;
      }
    },

    async searchPatientRegistration(data: IPatientRegistrationLookupParams) {
      try {
        const response =
          await new_api_public.post<IPatientRegistrationLookupResponse>(
            "/patient/register/lookup",
            data,
          );
        return response;
      } catch (error: any) {
        if (error?.response?.data?.message) {
          toast.error(error?.response?.data?.message);
        }
      }
    },

    async searchPatientRegistrationByCpfBirthDate(
      data: IPatientRegistrationLookupParams,
    ) {
      return await new_api_public.get<IPatientRegistrationLookupResponse>(
        "/patient/register/lookup-cpf-birthdate",
        {
          params: {
            cpf: data.cpf,
            birthDate: data.birthDate ?? data.birth_date,
          },
        },
      );
    },

    async completePatientRegistration(data: ICompletePatientRegistrationParams) {
      try {
        const response =
          await new_api_public.post<ICompletePatientRegistrationResponse>(
            "/patient/register",
            data,
          );
        if (response?.data?.message) {
          toast.success(response?.data?.message);
        }
        return response;
      } catch (error: any) {
        if (error?.response?.data?.message) {
          toast.error(error?.response?.data?.message);
        }
      }
    },

    async registerPatientAddress(data: IRegisterPatientAddressParams) {
      try {
        return await new_api_public.post(
          `/patient/register/address/${data.patientId}`,
          {
            registrationToken: data.registrationToken,
            cd_cep: data.cep.replace(/\D/g, ""),
            ds_endereco: data.address.trim(),
            ds_complemento: data.addressComplement?.trim() || null,
            ds_bairro: data.neighborhood.trim(),
            cd_municipio_ibge: data.municipalityCode.trim().slice(0, 6),
            sg_estado: data.state.trim().toUpperCase(),
            nr_endereco: data.addressNumber.trim(),
          },
        );
      } catch (error: any) {
        if (error?.response?.data?.message) {
          toast.error(error?.response?.data?.message);
        }
        throw error;
      }
    },

    async sendPatientValidationCode(data: ISendPatientValidationCodeParams) {
      try {
        const response =
          await new_api_public.post<ISendPatientValidationCodeResponse>(
            "/patient/validation/send-code",
            data,
          );
        if (response?.data?.message) {
          toast.success(response?.data?.message);
        }
        return response;
      } catch (error: any) {
        if (error?.response?.data?.message) {
          toast.error(error?.response?.data?.message);
        }
      }
    },

    async validatePatientValidationCode(
      data: IValidatePatientValidationCodeParams,
    ) {
      try {
        const response =
          await new_api_public.post<IValidatePatientValidationCodeResponse>(
            "/auth/validate/code",
            data,
          );
        if (response?.data?.message) {
          toast.success(response?.data?.message);
        }
        return response;
      } catch (error: any) {
        if (error?.response?.data?.message) {
          toast.error(error?.response?.data?.message);
        }
      }
    },
  };
}
